import { useCallback, useState } from 'react';

import { useUrlTiles } from '../../url-tiles';
import { useDeleteJobLevels } from '../api/use-delete-job-levels';
import { useDeleteJobRoles } from '../api/use-delete-job-roles';
import type { JobItemDetails } from '../api/use-job-item-details';
import { useUnarchiveJobLevels } from '../api/use-unarchive-job-levels';
import { useUnarchiveJobRoles } from '../api/use-unarchive-job-roles';
import { JSA_PRIMARY_DETAIL_TILE_SLOT_KEY } from '../url-tile-payloads';

type UseArchivedItemActionsProps = {
    readonly architectureId: string;
    readonly item: JobItemDetails;
    readonly itemType: 'job-level' | 'job-role';
    readonly onDeleteComplete?: () => void;
    readonly onRestoreComplete?: () => void;
};

type UseArchivedItemActionsResult = {
    readonly handleDelete: () => Promise<void>;
    readonly handleRestore: () => Promise<void>;
    readonly isDeleteDialogOpen: boolean;
    readonly setIsDeleteDialogOpen: (open: boolean) => void;
};

export const useArchivedItemActions = ({
    architectureId,
    item,
    itemType,
    onDeleteComplete,
    onRestoreComplete,
}: UseArchivedItemActionsProps): UseArchivedItemActionsResult => {
    const { mutateAsync: unarchiveRoles } = useUnarchiveJobRoles();
    const { mutateAsync: unarchiveLevels } = useUnarchiveJobLevels();
    const { mutateAsync: deleteRoles } = useDeleteJobRoles();
    const { mutateAsync: deleteLevels } = useDeleteJobLevels();
    const { closeTile } = useUrlTiles();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const closePrimaryDetailTile = useCallback((): void => {
        closeTile(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY);
    }, [closeTile]);

    const handleRestore = useCallback(async (): Promise<void> => {
        await (itemType === 'job-role'
            ? unarchiveRoles({ jobArchitectureId: architectureId, jobRoleIds: [item.id] })
            : unarchiveLevels({ jobArchitectureId: architectureId, jobLevelIds: [item.id] }));

        closePrimaryDetailTile();
        onRestoreComplete?.();
    }, [architectureId, closePrimaryDetailTile, item.id, itemType, onRestoreComplete, unarchiveLevels, unarchiveRoles]);

    const handleDelete = useCallback(async (): Promise<void> => {
        await (item.type === 'job-role' ? deleteRoles({ jobRoles: [item] }) : deleteLevels({ jobLevels: [item] }));

        setIsDeleteDialogOpen(false);
        closePrimaryDetailTile();
        onDeleteComplete?.();
    }, [closePrimaryDetailTile, deleteLevels, deleteRoles, item, onDeleteComplete]);

    return { handleDelete, handleRestore, isDeleteDialogOpen, setIsDeleteDialogOpen };
};
